<template>
  <!-- Mousedown here is to prevent the editable portion from losing focus when the element padding is clicked-->
  <span
    v-bind="$attrs"
    ref="containerElement"
    class="ui-inline-editable inline-block focus-within_cursor-auto border-box border border-transparent transition-colors"
    :title="value"
    role="input"
    :class="[{ error }, theme, disableOnMetaClick ? 'select-none' : 'cursor-text']"
    tabindex="0"
    @click="click"
    @dblclick="doubleClick"
    @mousedown="$emit('mousedown', $event)"
  >
    <div class="relative max-w-full">
      <div
        ref="inputElement"
        class="editable overflow-hidden overflow-ellipsis max-w-full focus_overflow-clip break-words"
        spellcheck="false"
        data-testid="input-element"
        :style="{ '--placeholder-text': `'${placeholder}'`, 'min-width': `${minwidth}` }"
        :contenteditable="!disableOnMetaClick"
        @click.exact="disableOnMetaClick = disabled"
        @click.shift.left.exact="($emit('shift-click', $event), (disableOnMetaClick = true))"
        @click.ctrl.left.exact="($emit('ctrl-click', $event), (disableOnMetaClick = true))"
        @click.meta.left.exact="($emit('ctrl-click', $event), (disableOnMetaClick = true))"
        @focus="focus"
        @blur="save"
        @keyup.esc="cancel(true)"
        @keyup="safariShiftEnter = false"
        @keydown.shift.enter="safariShiftEnter = true"
        @keydown.meta.enter="(insertLineBreakIfAllowed($event), (safariShiftEnter = true))"
        @keydown.ctrl.enter="insertLineBreakIfAllowed($event)"
        @beforeinput="beforeInput"
        @input="input"
        @contextmenu="handleContextMenu"
      />
      <ExclamationCircleIcon v-if="error" class="h-4 select-none text-red-800 ml-1" data-testid="error-icon" @mousedown.self.prevent="" />
    </div>
  </span>
</template>

<script lang="ts" setup>
import { nextTick } from 'vue'
import { ExclamationCircleIcon } from '@heroicons/vue/24/solid'
import { ClientJS } from 'clientjs'
import { contentEditableEvents } from '@/lib/content-editable'

const props = defineProps({
  placeholder: {
    type: String,
    default: 'Start typing\u2026',
  },
  value: {
    type: String,
    default: '',
  },
  error: {
    type: Boolean,
    default: false,
  },
  maxlength: {
    type: Number,
    default: 255,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  theme: {
    type: String,
    default: 'light',
  },
  minwidth: {
    type: String,
    default: 'inherit',
  },
  focusOnMount: {
    type: Boolean,
    default: false,
  },
  allowLineBreak: {
    type: Boolean,
    default: false,
  },
  noBlurElement: {
    type: String,
    default: null,
  },
})

defineOptions({
  inheritAttrs: false,
})

const emit = defineEmits([
  'focus',
  'blur',
  'input',
  'dblclick',
  'click',
  'save',
  'no-blur-save',
  'cancel',
  'shift-click',
  'ctrl-click',
  'clear',
  'mousedown',
  'enter-key',
  'update:value',
])

const inputElement = ref<HTMLElement | undefined>()
const isClicked = ref(false)
const containerElement = ref<HTMLElement | undefined>()
defineExpose({ containerElement, inputElement })
const dirty = ref(false)
const disableOnMetaClick = ref<boolean>(props.disabled)

// Since Safari emits a `insertParagraph` InputEvent on shift+enter rather than `insertLineBreak`
// like Chrome/FF, we need this discriminator to distinguish it from unmodified <enter>.
const safariShiftEnter = ref(false)

function shouldRemainFocused(e: FocusEvent) {
  const target = e.relatedTarget as Element | null
  const isWithinContainer = target ? containerElement.value?.contains(target) : false
  const isAllowableNoBlurElement = props.noBlurElement && target ? target.closest(props.noBlurElement) : false

  return isWithinContainer || isAllowableNoBlurElement
}

function triggerBlur() {
  if (inputElement.value) {
    inputElement.value.scrollLeft = 0
    inputElement.value.blur()
    inputElement.value.innerText = inputElement.value.innerText?.trim() ?? null
  }

  document.getSelection()?.removeAllRanges() // Needed for Safari

  emit('blur')
}

function save(e: FocusEvent) {
  nextTick(() => {
    if (disableOnMetaClick.value) return

    if (shouldRemainFocused(e)) {
      emit('no-blur-save')
      return
    }

    if (!inputElement.value?.innerText?.trim()) {
      emit('clear') // to handle saving an empty caption
      triggerBlur()
      return
    }

    if (dirty.value) {
      emit('save')
      dirty.value = false
    }
    triggerBlur()
  })
}

function cancel(explicit: boolean) {
  // "explicit" means the user pressed escape (explicitly canceling the operation)
  // vs just saved an empty input (implicitly canceling the operation)
  emit('cancel', { explicit })
  dirty.value = false
  // wait for value to reset
  nextTick(triggerBlur)
}

function handleEnter(e: Event) {
  if (e instanceof InputEvent && e.inputType === 'insertParagraph') {
    e.preventDefault()
    emit('enter-key')
    triggerBlur()
    isClicked.value = false
    return true
  }
}

const { handleInputEvent, insertLineBreakIfAllowed } = contentEditableEvents({
  maxlength: props.maxlength,
  allowLineBreak: props.allowLineBreak,
})

watch(
  [() => props.value, inputElement],
  ([val, element]) => {
    // Change is dependent on DOM value not ref value
    if (element && val !== element.innerText) {
      element.innerText = val
    }
  },
  { immediate: true },
)

watch(
  () => props.disabled,
  disabled => {
    disableOnMetaClick.value = disabled
  },
)

watch(
  [() => props.focusOnMount, inputElement],
  ([focusOnMount, inputElement]) => {
    if (focusOnMount && inputElement) {
      inputElement.focus()
      isClicked.value = true
    }
  },
  { immediate: true },
)

function input(_e: Event) {
  emit('input', inputElement.value?.innerText)
  emit('update:value', inputElement.value?.innerText)
}
function focus(e: FocusEvent) {
  if (disableOnMetaClick.value) return
  emit('focus', e)
}
function doubleClick(e: MouseEvent) {
  inputElement.value?.focus()
  isClicked.value = true
  emit('dblclick', e)
}
function click(e: MouseEvent) {
  emit('click', e)
}

function beforeInput(e: Event) {
  if (process.client) {
    const client = new ClientJS()
    if (!client.isSafari() || !safariShiftEnter.value) {
      if (handleEnter(e)) return
      if (handleInputEvent(e as InputEvent)) return
    }
    dirty.value = true
  }
}

function handleContextMenu(e: Event) {
  if (isClicked.value) {
    e.stopPropagation()
  }
}
</script>

<style lang="stylus" scoped>
/**
 * See assets/css/tailwind.css for default styles on this component
 * which can be overridden from the parent scope.
 */

.editable
  @apply outline-none

  &:empty,
  &:empty::after // for placeholder text
    @apply w-32 opacity-50

  &:empty
    // Needed for Firefox to keep line height
    &::before
      @apply block
      content '\00a0'

    // Insert placeholder text
    &::after
      @apply absolute top-0 left-0 block
      @apply overflow-ellipsis overflow-hidden min-w-inherit whitespace-nowrap
      content var(--placeholder-text)

    &:hover
      @apply opacity-100

.ui-inline-editable.enhance-placeholders
  .editable
    &:empty,
    &:empty::after
      @apply opacity-100
  &:focus-within
    .editable
      &:empty,
      &:empty::after
        @apply opacity-50

// Theme colours
.ui-inline-editable.light
  &.error
    @apply text-red-800

  &:hover
    @apply border-gray-300

  &:focus-within
    @apply border-gray-300 bg-white text-black

    &.error
      @apply border-red-500

  .editable
    caret-color black

.ui-inline-editable.dark
  &.error
    @apply text-red-800

  &:hover
    @apply border-gray-600

  &:focus-within
    @apply border-white text-white

    &.error
      @apply border-red-500

  .editable
    caret-color white

    &::selection
      background theme('colors.copper.700')

.ui-inline-editable.dark-3d-labels
  @apply border-0
  border-radius: 0.25em
  background: linear-gradient(180deg, #4C4C4C 0%, #181818 100%)

  .editable
    &:empty,
    &:empty::after // for placeholder text
      width: 9em // use ems to be relative to font size

  &.error
    @apply text-red-800

  &:hover
    @apply border-transparent

  &.selected
    @apply border-green-speaker border-2 bg-black text-white

  &:focus-within
    @apply border-green-speaker border-2 bg-black text-white

    &.error
      @apply border-red-500

  .editable
    caret-color white

    &::selection
      background theme('colors.copper.700')

.ui-inline-editable.copper-light
  &.error
    @apply text-red-800

  &:hover
    @apply border-copper-300

  &:focus-within
    @apply border-copper-700 bg-copper-100 text-black

    &.error
      @apply border-red-500

  .editable
    caret-color black

    &::selection
      background theme('colors.copper.700')
</style>
